import { makeAutoObservable, action, runInAction } from "mobx";
import { User } from "@/models/User";
import { routes } from "@/router/Routes";
import { UnknownUser } from "@/models/UnknownUser";
import { PasswordChange } from "@/models/PasswordChange";
import { logger } from "@/util/logger";
import type { JwtPayload } from "@/util/globalTypes";
import { getCookie } from "@/util/cookieUtils";
import { decodeJwtToken } from "@/util/AppUtils";
import { getStoredReferrer, clearStoredReferrer } from "@/hooks/useReferrer";
import { interactionStore, profileStore } from "./Stores";
import type { AuthService } from "@/services/AuthService";
import { authService } from "@/services/Services";
export class AuthStore {
  private static instance: AuthStore;
  private authService: AuthService;

  public unknownUser: UnknownUser = new UnknownUser();
  public user: User | null = null;
  public passwordChange: PasswordChange = new PasswordChange();
  public decodedToken: JwtPayload | null = null;
  public isValidating = false;
  public securityCode: string[] = ["", "", "", "", "", ""];
  public otpSession = "";
  public loginFailed = false;
  public expectingRegistrationCode = false;
  public passwordResetStatus?: "success" | "error";

  public userHaveProfile = false;

  private constructor() {
    makeAutoObservable(this);
    this.authService = authService;
  }

  public set authenticatedUser(value: User | null) {
    this.user = value;
  }

  public static getInstance(): AuthStore {
    if (!AuthStore.instance) {
      AuthStore.instance = new AuthStore();
    }
    return AuthStore.instance;
  }

  public get isAuthenticated(): boolean {
    return this.user !== null;
  }

  public get canSignup(): boolean {
    logger.log("AuthStore.canSignup()", this.unknownUser.canSignup, this.expectingRegistrationCode);
    return this.unknownUser.canSignup && !this.expectingRegistrationCode;
  }

  public get canConfirmSignup() {
    return this.expectingRegistrationCode && this.unknownUser.registrationCode?.length === 6;
  }

  @action
  private updateUserState() {
    // With HTTP-only cookies, we don't need to store tokens in memory
    // Create user with placeholder tokens
    const idToken = getCookie("id_token");
    const currentSession = getCookie("current_session");
    const decodedToken = decodeJwtToken(idToken);
    if (!idToken || !decodedToken || !currentSession) {
      logger.error("No valid token found");
      return;
    }
    this.user = new User(idToken, currentSession, decodedToken?.email, new Date().getTime().toString());
    profileStore.setToken(idToken);
  }

  @action
  public async login(security_code?: string, otp_session?: string): Promise<void> {
    if (!this.unknownUser.canLogin) {
      throw new Error("Can't login, bad form data");
    }

    try {
      runInAction(() => {
        interactionStore.loading = true;
      });

      const response = await this.authService.login(this.unknownUser.email ?? "", this.unknownUser.password ?? "", security_code, otp_session);
      const { data } = response;

      if (data.otp_session) {
        this.otpSession = data.otp_session;
        return;
      }

      const { redirect_to } = data;
      logger.log("Login successful. Cookies set");
      const id_token = getCookie("id_token");
      const current_session = getCookie("current_session");

      if (id_token && current_session) {
        // Decode the token to get user information
        const decodedToken = decodeJwtToken(id_token);

        if (decodedToken) {
          // Create user object with the tokens from the response
          runInAction(() => {
            this.user = new User(id_token, current_session, decodedToken.email, new Date().getTime().toString());
            this.otpSession = "";
            this.decodedToken = decodedToken;
          });

          logger.log("User authenticated with tokens from response");

          // Get stored referrer from localStorage
          const storedReferrer = getStoredReferrer();

          // Determine redirect destination based on new flow
          const redirectDestination = storedReferrer && storedReferrer.trim() !== "" ? redirect_to || routes.pages.profile : routes.pages.profile;

          logger.log(storedReferrer ? `Using backend redirect with referrer: ${redirectDestination}` : "No referrer found, redirecting to profile");

          // Clear the stored referrer after using it
          clearStoredReferrer();

          // Set the navigation destination
          interactionStore.navigate = redirectDestination;
          return;
        }
      }
    } catch (error) {
      logger.error("Login failed:", error);
    } finally {
      interactionStore.loading = false;
    }
  }

  @action
  public logout(): void {
    this.user = null;
    interactionStore.navigate = routes.pages.login;
  }

  public async signup(): Promise<void> {
    console.log("AuthStore.signup()", this.canSignup);
    if (this.canSignup) {
      interactionStore.loading = true;
      await this.authService
        .register(this.unknownUser.email ?? "", this.unknownUser.password ?? "")
        .then(response => {
          console.log("Signup res:", response);
          this.expectingRegistrationCode = true;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          interactionStore.loading = false;
        });
    }
  }

  public async confirmSignup(): Promise<void> {
    if (this.canConfirmSignup) {
      // TODO: Confirm signup logic
      this.authService
        .confirmRegister(this.unknownUser.name ?? "", this.unknownUser.registrationCode ?? "")
        .then(_ => {
          interactionStore.navigate = routes.pages.login;
        })
        .catch(error => {
          console.log(error);
        });
    }
  }

  public async requestPasswordReset(): Promise<void> {
    if (this.unknownUser.canResetPassword) {
      interactionStore.loading = true;
      await this.authService
        .passwordReset(this.unknownUser.email ?? "")
        .then(response => {
          this.passwordResetStatus = "success";
          console.log("Reset password res:", response);
        })
        .catch(error => {
          this.passwordResetStatus = "error";
          console.log(error);
        })
        .finally(() => {
          interactionStore.loading = false;
        });
    }
  }

  public async changePassword(): Promise<void> {
    if (this.unknownUser.canCreateNewPassword) {
      interactionStore.loading = true;
      this.authService
        .passwordResetConfirm(this.unknownUser.passwordResetCode ?? "", this.unknownUser.password ?? "")
        .then(response => {
          console.log("Change password res:", response);
          interactionStore.navigate = routes.pages.login;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          interactionStore.loading = false;
        });
    }
  }

  public async createNewPassword(): Promise<void> {
    if (this.passwordChange.canSubmit) {
      this.authService
        .passwordChange(this.passwordChange.oldPassword ?? "", this.passwordChange.newPassword ?? "")
        .then(response => {
          console.log("Create new password res:", response);
          interactionStore.navigate = routes.pages.login;
        })
        .catch(error => {
          console.log(error);
        });
    }
  }

  public async refreshToken(): Promise<void> {
    if (!this.user) {
      logger.error("No user found for token refresh");
      return;
    }
    logger.log("Attempting token refresh");

    try {
      await this.authService.refreshToken();

      // Update user state
      this.updateUserState();
    } catch (error) {
      logger.error("Token refresh failed:", error);
      this.logout();
    }
  }
}
